import { createFileRoute, Link } from "@tanstack/react-router";
import { LoanCategoryPage } from "@/components/laneguide/LoanCategoryPage";
import { PAGE_FAQ } from "@/components/laneguide/PageFaq";

const SITE = "https://svensklaneguide.se";
const URL = `${SITE}/mikrolan-utan-uc-1000-kr`;
const TITLE = "Mikrolån Utan UC Från 1000 kr – jämför långivare | Låneguide";
const DESC =
  "Mikrolån utan UC från 1000 kr. Jämför långivare som använder Creditsafe eller Bisnode, med snabb utbetalning och bedömning trots låg kreditvärdighet.";

export const Route = createFileRoute("/mikrolan-utan-uc-1000-kr")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { name: "robots", content: "index, follow" },
      { property: "og:title", content: "Mikrolån Utan UC Från 1000 kr" },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Mikrolån Utan UC Från 1000 kr" },
      { name: "twitter:description", content: DESC },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Hem", item: SITE },
            { "@type": "ListItem", position: 2, name: "Lån utan UC", item: `${SITE}/lan-utan-uc` },
            { "@type": "ListItem", position: 3, name: "Mikrolån utan UC från 1000 kr", item: URL },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: (PAGE_FAQ["lan-utan-uc"] ?? []).map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: () => (
    <LoanCategoryPage
      page="mikrolan-utan-uc-1000"
      productLabel="Mikrolån utan UC från 1 000 kr"
      headingBefore="Mikrolån Utan UC"
      underlined="Från 1000 kr"
      headingAfter=""
      subtitle={
        <>
          Vill du undvika ännu en UC-förfrågan? Vi förstår. Här listar vi långivare som beviljar{" "}
          <Link to="/nyhet/$slug" params={{ slug: "mikrolan-utan-uc-1000-kr" }} className="text-primary underline underline-offset-2">
            mikrolån utan UC från 1 000 kr
          </Link>{" "}
          och som gör kreditprövningen via Creditsafe, Bisnode eller D&B. Aktörerna med lägst belopp ligger
          högst upp, därefter följer långivare som startar på 2 000–3 000 kr.
        </>
      }
      lockedFilter="utan-uc"
      featuredTitleTemplate={(m) => `Toppval för mikrolån utan UC i ${m}`}
      belowList={<BelowContent />}
    />
  ),
});

function BelowContent() {
  return (
    <section className="mx-auto max-w-4xl px-4 py-14 sm:px-6 lg:px-8">
      <div className="rounded-lg border border-border bg-card p-6 sm:p-10">
        <h2 className="font-display text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
          Mikrolån utan UC – vad du bör veta innan du ansöker
        </h2>
        <p className="mt-4 text-[15px] leading-relaxed text-foreground/80">
          Har du fått nej trots att du sköter dina räkningar? Det känns orättvist när historiken väger tyngre
          än hur ekonomin ser ut i dag. Ett mikrolån utan UC kan vara vägen runt det – men det är ett verktyg,
          inte en genväg.
        </p>

        <div className="mt-10 space-y-8">
          <div>
            <h3 className="font-display text-xl font-extrabold text-foreground">
              Vilka kreditupplysningar används i stället
            </h3>
            <p className="mt-2 text-[15px] leading-relaxed text-foreground/80">
              Långivarna hämtar uppgifter hos Creditsafe, Bisnode, Syna eller D&B. Underlaget liknar UC:s, men
              modellerna väger ofta din nuvarande inkomst tyngre än historiken – vilket gör att fler ansökningar
              godkänns. Din ansökan syns inte i UC:s register, men den registreras hos det bolag som används.
            </p>
          </div>
          <div>
            <h3 className="font-display text-xl font-extrabold text-foreground">
              Lån helt utan kreditupplysning finns inte
            </h3>
            <p className="mt-2 text-[15px] leading-relaxed text-foreground/80">
              Kreditprövning är obligatorisk enligt konsumentkreditlagen. Marknadsföring om ”garanterat lån”
              eller ”lån utan kreditupplysning” är ett tydligt varningstecken. Kontrollera alltid att bolaget
              står under tillsyn hos{" "}
              <a
                href="https://www.fi.se"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline underline-offset-2"
              >
                Finansinspektionen
              </a>
              .
            </p>
          </div>
          <div>
            <h3 className="font-display text-xl font-extrabold text-foreground">
              Räkna på totalkostnaden – exempel
            </h3>
            <p className="mt-2 text-[15px] leading-relaxed text-foreground/80">
              Lånar du 2 000 kr i tre månader med 26 procents ränta och 29 kr i aviavgift blir kostnaden runt
              174 kr. Tillkommer en uppläggningsavgift på 195 kr stiger totalen till cirka 2 370 kr. Det är
              avgifterna, inte procenten, som avgör på små belopp.
            </p>
          </div>
          <div>
            <h3 className="font-display text-xl font-extrabold text-foreground">
              Med betalningsanmärkning eller låg inkomst
            </h3>
            <p className="mt-2 text-[15px] leading-relaxed text-foreground/80">
              Flera aktörer gör en helhetsbedömning där aktuell inkomst väger tyngst. Har du pågående utmätning
              hos Kronofogden är chansen liten – då är kommunens kostnadsfria budget- och skuldrådgivning ett
              bättre första steg. Vi har samlat råden i guiden om{" "}
              <Link
                to="/nyhet/$slug"
                params={{ slug: "betalningsanmarkning-lan" }}
                className="text-primary underline underline-offset-2"
              >
                betalningsanmärkning och lån
              </Link>
              .
            </p>
          </div>
          <div>
            <h3 className="font-display text-xl font-extrabold text-foreground">
              Kontokredit i stället för nytt lån varje gång
            </h3>
            <p className="mt-2 text-[15px] leading-relaxed text-foreground/80">
              Dyker behovet upp några gånger per år blir en kontokredit oftast billigare. Du betalar bara för
              utnyttjat utrymme och slipper en ny uppläggningsavgift varje gång. Behöver du större belopp är ett{" "}
              <Link to="/privatlan" className="text-primary underline underline-offset-2">
                privatlån
              </Link>{" "}
              nästan alltid billigare, även om det innebär en UC-förfrågan.
            </p>
          </div>
          <div>
            <h3 className="font-display text-xl font-extrabold text-foreground">Så snabbt kommer pengarna</h3>
            <p className="mt-2 text-[15px] leading-relaxed text-foreground/80">
              Många aktörer utan UC har helautomatiserad handläggning och betalar ut samma dag. Delar du bank
              med långivaren, eller om realtidsbetalning används, kan pengarna vara framme inom minuter – se
              även{" "}
              <Link to="/sms-lan-1000-kr-swish" className="text-primary underline underline-offset-2">
                akut SMS-lån via Swish
              </Link>
              .
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
